import { useRef, useState } from "react";
import { toast } from "sonner";
import { Check, ImagePlus, Loader2, X } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";

export default function ImageInserter({ onInsert, testId = "img", uploadUrl = "/admin/upload" }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState("");
  const [width, setWidth] = useState("");
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post(uploadUrl, body, { headers: { "Content-Type": "multipart/form-data" } });
      setUrl(data.url);
      toast.success("Image téléversée");
    } catch (error) {
      toast.error(apiError(error, "Upload impossible"));
    } finally {
      setUploading(false);
    }
  };

  const insert = () => {
    if (!url.trim()) return;
    const opts = width && Number(width) ? `|width=${width}` : "";
    onInsert(`![image](${url.trim()}${opts})`);
    setUrl("");
    setWidth("");
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid={`insert-image-btn-${testId}`}
        onClick={() => setOpen(true)}
        className="mt-1.5 flex items-center gap-1.5 text-xs font-bold text-emerald-600 hover:underline"
      >
        <ImagePlus className="h-3.5 w-3.5" /> Insérer une image
      </button>
    );
  }

  return (
    <div className="mt-2 space-y-2 rounded-xl border border-emerald-300/60 bg-emerald-50/50 p-3" data-testid={`image-panel-${testId}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          data-testid={`image-file-${testId}`}
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
        />
        <button
          type="button"
          data-testid={`image-upload-btn-${testId}`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 rounded-lg border border-line bg-white px-3 py-2 text-xs font-bold text-ink hover:border-emerald-500 disabled:opacity-60"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
          {uploading ? "Téléversement..." : "Téléverser une image"}
        </button>
        <input
          data-testid={`image-url-${testId}`}
          placeholder="ou collez l'URL de l'image"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="h-9 min-w-[220px] flex-1 rounded-lg border border-line px-3 text-xs focus:border-emerald-500 focus:outline-none"
        />
        <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600">
          Largeur
          <input
            type="number"
            min={50}
            max={1200}
            data-testid={`image-width-${testId}`}
            placeholder="auto"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            className="h-8 w-20 rounded-lg border border-line px-2 text-xs"
          />
          px
        </label>
      </div>
      {url && (
        <img src={fileUrl(url)} alt="" className="max-h-32 rounded-lg border border-line bg-white p-1" />
      )}
      <div className="flex gap-2">
        <button
          type="button"
          data-testid={`image-insert-${testId}`}
          onClick={insert}
          disabled={!url.trim() || uploading}
          className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
        >
          <Check className="h-3.5 w-3.5" /> Insérer l'image
        </button>
        <button
          type="button"
          data-testid={`image-cancel-${testId}`}
          onClick={() => setOpen(false)}
          className="flex items-center gap-1.5 rounded-lg border border-line px-4 py-2 text-xs font-bold text-slate-600"
        >
          <X className="h-3.5 w-3.5" /> Annuler
        </button>
      </div>
    </div>
  );
}
